
import React, { useEffect, useMemo, useState } from 'react';
import { localizedValue } from '../localization/presentationLanguage';
const feedText = (es, en) => localizedValue({ es, en });

function normalizeImageUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const normalized = raw
    .replace(/^http:\/\/localhost:5001/i, 'https://api.zodiacus.app')
    .replace(/^http:\/\/127\.0\.0\.1:5001/i, 'https://api.zodiacus.app');

  if (/^https?:\/\//i.test(normalized)) return normalized;
  if (normalized.startsWith('/')) return normalized;
  return '';
}

function normalizeDimension(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

export default function FeedPostImage({ post, image, isMobile = false, onOpen }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [pressed, setPressed] = useState(false);

  const imageUrl = useMemo(() => normalizeImageUrl(image?.url), [image?.url]);
  const thumbUrl = useMemo(() => normalizeImageUrl(image?.thumbUrl), [image?.thumbUrl]);
  const altText = useMemo(() => {
    const explicitAlt = String(image?.alt || '').trim();
    if (explicitAlt) return explicitAlt;

    const authorName = String(post?.author?.fullName || '').trim();
    if (authorName) return `Imagen compartida por ${authorName}`;
    return 'Imagen del post';
  }, [image?.alt, post?.author?.fullName]);

  const width = useMemo(() => normalizeDimension(image?.width), [image?.width]);
  const height = useMemo(() => normalizeDimension(image?.height), [image?.height]);
  const aspectRatio = useMemo(() => {
    if (!width || !height) return null;
    return `${width} / ${height}`;
  }, [width, height]);

  const previewUrl = imageUrl || thumbUrl;
  const canOpen = typeof onOpen === 'function' && !!previewUrl && !error;

  useEffect(() => {
    setLoaded(false);
    setError(false);
    setPressed(false);
  }, [imageUrl, thumbUrl]);

  if (!previewUrl) return null;

  const handleActivate = () => {
    if (!canOpen) return;
    onOpen({
      post,
      image: {
        ...image,
        url: imageUrl || thumbUrl,
        thumbUrl: thumbUrl || imageUrl,
        alt: altText,
      },
    });
  };

  return (
    <div
      className="feed-post-image"
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      aria-label={canOpen ? 'Abrir imagen del post' : undefined}
      onClick={canOpen ? handleActivate : undefined}
      onKeyDown={canOpen ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleActivate();
        }
      } : undefined}
      onMouseDown={canOpen ? () => setPressed(true) : undefined}
      onMouseUp={canOpen ? () => setPressed(false) : undefined}
      onMouseLeave={canOpen ? () => setPressed(false) : undefined}
      onTouchStart={canOpen ? () => setPressed(true) : undefined}
      onTouchEnd={canOpen ? () => setPressed(false) : undefined}
      style={{
        position: 'relative',
        width: '100%',
        borderRadius: isMobile ? 16 : 20,
        overflow: 'hidden',
        border: error
          ? '1px solid rgba(255,120,120,0.14)'
          : '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(255,255,255,0.04)',
        boxShadow: error
          ? '0 10px 24px rgba(0,0,0,0.12)'
          : pressed
            ? '0 10px 22px rgba(0,0,0,0.16), 0 0 0 1px rgba(255,224,130,0.05) inset'
            : '0 14px 30px rgba(0,0,0,0.16), 0 0 0 1px rgba(255,255,255,0.03) inset',
        transform: pressed ? 'scale(0.995)' : 'scale(1)',
        transition: 'transform 160ms ease, box-shadow 180ms ease, border-color 180ms ease',
        cursor: canOpen ? 'zoom-in' : 'default',
        outline: 'none',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: aspectRatio || (isMobile ? '4 / 5' : '16 / 10'),
          background:
            'linear-gradient(145deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
        }}
      >
        {!loaded && !error && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                'linear-gradient(120deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: 'rgba(255,255,255,0.64)',
                letterSpacing: 0.24,
              }}
            >
              {feedText('Cargando imagen...', 'Loading image...')}
            </span>
          </div>
        )}

        {!error ? (
          <img
            src={previewUrl}
            alt={altText}
            loading="lazy"
            decoding="async"
            draggable={false}
            referrerPolicy="no-referrer"
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: loaded ? 1 : 0,
              transition: 'opacity 240ms ease, transform 220ms ease',
              transform: loaded ? 'scale(1)' : 'scale(1.02)',
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }}
          />
        ) : (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px 16px',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.68)',
              fontSize: 12.5,
              fontWeight: 800,
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.015))',
            }}
          >
            No se pudo cargar la imagen
          </div>
        )}

        {!error ? (
          <>
            <div
              aria-hidden="true"
              style={{
                pointerEvents: 'none',
                position: 'absolute',
                inset: 0,
                background:
                  'radial-gradient(180px 80px at 50% 0%, rgba(255,224,130,0.10), transparent 60%)',
              }}
            />
            <div
              aria-hidden="true"
              style={{
                pointerEvents: 'none',
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(180deg, rgba(8,6,16,0) 52%, rgba(8,6,16,0.16) 100%)',
              }}
            />
          </>
        ) : null}

        {canOpen && !error ? (
          <div
            aria-hidden="true"
            style={{
              pointerEvents: 'none',
              position: 'absolute',
              right: 10,
              bottom: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              borderRadius: 999,
              background: 'rgba(8,6,16,0.56)',
              border: '1px solid rgba(255,255,255,0.10)',
              color: '#FFF5DC',
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.16,
              boxShadow: '0 8px 18px rgba(0,0,0,0.18)',
            }}
          >
            <span aria-hidden="true">✦</span>
            Ver
          </div>
        ) : null}
      </div>
    </div>
  );
}
